/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { AiCoach_Action as AiCoachAction } from '__generated__/graphql-types';
import classNames from 'classnames';

import { get as getUser } from 'js/lib/user';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import { CopySingleResponseLazy } from 'bundles/ai-coach-platform/components/building-blocks';
import CoachIcon from 'bundles/ai-course-coach/assets/CoachIcon';
// eslint-disable-next-line import/no-cycle
import {
  CoachRecommendations,
  Feedback,
  LearnerAvatar,
  ProgressiveLoader,
} from 'bundles/ai-course-coach/components/Conversation/components/Messages';
import HomeView from 'bundles/ai-course-coach/components/HomeView/HomeView';
import { MESSAGE_SENDER, getErrorMessageText } from 'bundles/ai-course-coach/components/constants';
import { markAllMessagesRead } from 'bundles/ai-course-coach/store/actions';
import type { CustomCompMessage, GlobalState, Link, MessageTypes } from 'bundles/ai-course-coach/store/types';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';
import type {
  CoachPersona,
  HomeView as HomeViewType,
  SendMessageFunc,
  SenderType,
} from 'bundles/ai-course-coach/utils/types';

import _t from 'i18n!nls/learning-assistant-chat-ai';

const isClientResponse = (sender: SenderType | string) => sender === MESSAGE_SENDER.CLIENT;
const isCoachResponse = (sender: SenderType | string) => sender === MESSAGE_SENDER.RESPONSE;

export type HideMessageType = ({
  message,
  index,
  messages,
}: {
  message: MessageTypes | Link | CustomCompMessage;
  index: number;
  messages: Array<MessageTypes | Link | CustomCompMessage>;
}) => boolean;

type ForceMessageLoaderProps =
  // Using discriminated union to ensure only one of these props can be used at a time
  | { forceHideMessageLoader?: boolean; forceShowMessageLoader?: never } // either hide loader
  | { forceHideMessageLoader?: never; forceShowMessageLoader?: boolean } // or show loader
  | { forceHideMessageLoader?: undefined; forceShowMessageLoader?: undefined }; // or neither

type Props = {
  profileClientAvatar?: string;
  homeView?: HomeViewType;
  sendMessage: SendMessageFunc;
  courseId?: string;
  hideMessage?: HideMessageType;
  coachPersona?: CoachPersona;
  chatSessionId?: string; // when this prop is defined, only messages from this chat session will be shown
} & ForceMessageLoaderProps;

const styles = {
  messagesContainer: css`
    /* Hide scrollbar for Chrome, Safari and Opera */

    ::-webkit-scrollbar {
      display: none;
    }

    height: 100%;
    max-height: none;

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    ${breakpoints.down('xs')} {
      height: 100%;
      max-height: none;
    }
  `,
  message: css`
    padding: 0 var(--cds-spacing-200);
    margin: var(--cds-spacing-200) 0;
    word-wrap: break-word;

    svg {
      flex-shrink: 0;
    }

    &.coach-message-response {
      border-radius: 0;
      scroll-margin-top: var(--cds-spacing-150);
    }
  `,
  messageContainer: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  senderName: css`
    color: var(--cds-color-grey-950);
    font-weight: 600;
  `,
};

const MessageContainer = ({
  avatar,
  name,
  children,
}: {
  /**
   * Message sender avatar
   */
  avatar?: React.ReactNode;
  /**
   * Message sender name
   */
  name: string;
  /**
   * Message content
   */
  children: React.ReactNode;
}) => {
  return (
    <div className="coach-message-container" css={styles.messageContainer}>
      {avatar}
      <Grid
        container
        css={css`
          overflow: auto;
          display: flex;
          flex-direction: column;
          gap: 0;
        `}
      >
        <Grid item>
          <Typography2 component="h4" variant="subtitleMedium" css={styles.senderName}>
            {name}
          </Typography2>
        </Grid>

        <Grid
          item
          css={css`
            max-width: 100%;
          `}
        >
          {children}
        </Grid>
      </Grid>
    </div>
  );
};

const CoachLoader = ({
  action,
  coachAvatar,
  coachName,
}: {
  action?: AiCoachAction;
  coachAvatar: React.ReactNode;
  coachName: string;
}): React.ReactElement => {
  return (
    <div className="coach-message" css={styles.message} role="status" aria-live="polite">
      <MessageContainer avatar={coachAvatar} name={coachName}>
        <ProgressiveLoader action={action} />
      </MessageContainer>
    </div>
  );
};

const Messages = ({
  profileClientAvatar,
  coachPersona,
  homeView,
  sendMessage,
  courseId,
  hideMessage,
  chatSessionId,
  forceHideMessageLoader,
  forceShowMessageLoader,
}: Props) => {
  const dispatch = useDispatch();
  const { messages, messageLoader, showChat, badgeCount, activeChatSessionId } = useSelector((state: GlobalState) => {
    const chatSessionMessages = chatSessionId && state.messages.historicalMessagesMap[chatSessionId];
    return {
      messages: chatSessionMessages || state.messages.messages,
      badgeCount: state.messages.badgeCount,
      messageLoader: state.behavior.messageLoader,
      showChat: state.behavior.showChat,
      activeChatSessionId: state.sessionState.chatSessionId,
    };
  });

  const showMessageLoader = forceShowMessageLoader || messageLoader;
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (showChat && badgeCount) {
      dispatch(markAllMessagesRead());
    }
  }, [dispatch, badgeCount, showChat, messages]);

  const messageCount = messages.length;

  // scroll latest message into view every time `messageCount` updates i.e. new message is added
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [messageCount, forceShowMessageLoader]);

  const getLastMessageAction = () => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      return lastMessage.action;
    }
    return undefined;
  };

  const getComponentToRender = (message: MessageTypes | Link | CustomCompMessage) => {
    const ComponentToRender = message.component;
    if (message.type === 'component') {
      return <ComponentToRender {...message.props} />;
    }
    return <ComponentToRender message={message} />;
  };

  const { fullName: userName, id: userId } = getUser();
  const userFirstName = firstNameFromFullName(userName);

  const learnerAvatarToRender = <LearnerAvatar imageUrl={profileClientAvatar} name={userFirstName} />;
  const { name: coachName, avatar: coachAvatar } = coachPersona || {};
  const coachAvatarToRender = coachAvatar || <CoachIcon size="medium" />;
  const coachNameToRender = coachName || 'Coach';

  const isEmptyState = !messages?.length;

  if (isEmptyState) {
    return (
      <HomeView
        sendMessage={sendMessage}
        quickMessages={homeView?.quickMessages}
        title={homeView?.title}
        subtitle={homeView?.subtitle}
      />
    );
  }

  return (
    // a11y implementation: https://www.w3.org/WAI/WCAG21/working-examples/aria-role-log/chatlog.html
    // see COACH-1236
    <div role="log">
      <div
        id="messages"
        ref={messageContainerRef}
        css={styles.messagesContainer}
        aria-label={_t('Coach chat messages')}
        role="list"
      >
        {messages?.map((message, index) => {
          const { timestamp, sender } = message;

          const isLastMessage = index === messages.length - 1;
          const isCoachTextResponse = isCoachResponse(sender) && message.type === 'text';
          // only allow Coach text messages to be copied
          const showCopyMessage = isCoachTextResponse;
          const isErrorMessage = isCoachTextResponse && (message as MessageTypes).text === getErrorMessageText();
          const isCoachMessage = isCoachResponse(sender);
          const isClientMessage = isClientResponse(sender);

          if (hideMessage && hideMessage({ message, index, messages })) {
            return null;
          }

          return (
            <div
              className={classNames('coach-message', {
                'coach-message-client': isClientMessage,
                'coach-message-response': isCoachMessage,
              })}
              key={isCoachMessage ? message.customId : `${userId}-${timestamp.valueOf()}`}
              css={styles.message}
              ref={isLastMessage ? lastMessageRef : null}
              role="listitem"
            >
              <MessageContainer
                avatar={isClientMessage ? learnerAvatarToRender : coachAvatarToRender}
                name={isClientMessage ? userFirstName : coachNameToRender}
              >
                {getComponentToRender(message)}
                {isCoachMessage && (
                  <CoachRecommendations
                    message={message}
                    sendMessage={sendMessage}
                    courseId={courseId}
                    showRecommendedActions={isLastMessage}
                  />
                )}
                {/* hide message actions for Coach error messages */}
                {!isErrorMessage && (
                  <div
                    className="coach-message-actions"
                    css={css`
                      display: flex;
                      justify-content: end;
                      align-items: center;
                    `}
                  >
                    <React.Suspense>
                      {showCopyMessage && <CopySingleResponseLazy message={(message as MessageTypes).text} />}
                    </React.Suspense>
                    <Feedback message={message as MessageTypes} />
                  </div>
                )}
              </MessageContainer>
            </div>
          );
        })}
        {!forceHideMessageLoader && showMessageLoader && (activeChatSessionId === chatSessionId || !chatSessionId) && (
          <CoachLoader
            action={getLastMessageAction()}
            coachAvatar={coachAvatarToRender}
            coachName={coachNameToRender}
          />
        )}
      </div>
    </div>
  );
};

export default Messages;
